/**
 * JobManager — Extracted from App class.
 * Handles all job CRUD operations, AI parsing, optimization, and publishing.
 */
import { AppEventType } from './appEvents';
export class JobManager {
    constructor(deps) {
        this.deps = deps;
    }
    get api() { return this.deps.getApi(); }
    get cache() { return this.deps.dataCache; }
    get events() { return this.deps.eventEmitter; }
    get logger() { return this.deps.logger; }
    getFromCache() {
        return this.cache.getJobs() || [];
    }
    async refresh() {
        this.deps.ensureAuthenticated();
        const response = await this.deps.makeApiCallWithRetry(() => this.api.getJobs());
        this.cache.updateData({ jobs: response.jobs, timestamp: Date.now() });
        const event = { jobs: response.jobs, timestamp: new Date() };
        this.events.emit(AppEventType.JOBS_LOADED, event);
        return response.jobs;
    }
    async getById(id) {
        try {
            const cached = this.cache.getJob(id);
            if (cached)
                return cached;
            const response = await this.deps.makeApiCallWithRetry(() => this.api.getJobs());
            const job = response.jobs.find(j => j.id === id);
            if (!job)
                throw new Error(`Job with id ${id} not found`);
            return job;
        }
        catch (error) {
            this.deps.handleError('getJob', this.deps.toError(error));
            throw error;
        }
    }
    async create(jobData) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.createJob(jobData));
            const job = response.job;
            this.deps.telemetryService.trackJobCreated({ jobTitle: jobData.description.title });
            this.cache.updateJob(job);
            this.events.emit(AppEventType.JOB_CREATED, { job });
            return job;
        }
        catch (error) {
            this.deps.handleError('createJob', this.deps.toError(error));
            throw error;
        }
    }
    async createFromUrl(url) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.createJobFromUrl(url));
            const job = response.job;
            this.cache.updateJob(job);
            this.events.emit(AppEventType.JOB_CREATED, { job, timestamp: new Date() });
            return job;
        }
        catch (error) {
            this.deps.handleError('createJobFromUrl', this.deps.toError(error));
            throw error;
        }
    }
    async createFromText(text) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.createJobFromText(text));
            const job = response.job;
            this.cache.updateJob(job);
            this.events.emit(AppEventType.JOB_CREATED, { job, timestamp: new Date() });
            return job;
        }
        catch (error) {
            this.deps.handleError('createJobFromText', this.deps.toError(error));
            throw error;
        }
    }
    async update(id, data) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateJob(id, data));
            const job = response.job;
            this.cache.updateJob(job);
            // Patch candidates if the server synced questions
            if (response.candidates) {
                for (const candidate of response.candidates) {
                    this.cache.updateCandidate(candidate);
                }
                this.events.emit(AppEventType.CANDIDATES_LOADED, { candidates: this.cache.getCandidates(), timestamp: new Date() });
            }
            this.events.emit(AppEventType.JOB_UPDATED, { job, timestamp: new Date() });
            return job;
        }
        catch (error) {
            this.deps.handleError('updateJob', this.deps.toError(error));
            throw error;
        }
    }
    async delete(id) {
        this.deps.ensureAuthenticated();
        try {
            await this.deps.makeApiCallWithRetry(() => this.api.deleteJob(id));
            this.cache.removeJob(id);
            this.events.emit(AppEventType.JOB_DELETED, { jobId: id, timestamp: new Date() });
        }
        catch (error) {
            this.deps.handleError('deleteJob', this.deps.toError(error));
            throw error;
        }
    }
    async publish(jobId, publish) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.publishJob(jobId, publish));
            const job = response.job;
            this.cache.updateJob(job);
            this.events.emit(AppEventType.JOB_UPDATED, { job, timestamp: new Date() });
            return job;
        }
        catch (error) {
            this.deps.handleError('publishJob', this.deps.toError(error));
            throw error;
        }
    }
    async optimizeScoring(jobId, jobDescription, additionalRequirements, answers) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.optimizeJobScoring({ jobId, jobDescription, additionalRequirements, answers }));
        }
        catch (error) {
            this.deps.handleError('optimizeJobScoring', this.deps.toError(error));
            throw error;
        }
    }
    async parseDescription(jobDescriptionText) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.parseJobDescription(jobDescriptionText));
        }
        catch (error) {
            this.deps.handleError('parseJobDescription', this.deps.toError(error));
            throw error;
        }
    }
    async convertPendingSession(sessionId) {
        this.deps.ensureAuthenticated();
        this.logger.info('Converting pending session:', sessionId);
        try {
            const data = await this.deps.makeApiCallWithRetry(() => this.api.convertPendingSession(sessionId));
            this.logger.info('Pending session converted:', { jobId: data.job?.id, candidateCount: data.candidates?.length });
            if (data.job) {
                this.cache.updateJob(data.job);
                this.events.emit(AppEventType.JOB_CREATED, { job: data.job });
            }
            if (data.candidates && data.candidates.length > 0) {
                for (const candidate of data.candidates) {
                    this.cache.updateCandidate(candidate);
                }
                this.events.emit(AppEventType.CANDIDATES_LOADED, { candidates: data.candidates, timestamp: new Date() });
            }
            return data;
        }
        catch (error) {
            this.deps.handleError('convertPendingSession', this.deps.toError(error));
            throw error;
        }
    }
}
